export type ManageSceneKind = 'photo' | 'video';

// Mirrors the gallery-service photo-sort mode set. 'manual' = the drag order.
export type PhotoSortMode =
  | 'manual'
  | 'uploadedNew'
  | 'uploadedOld'
  | 'shotAtNew'
  | 'shotAtOld'
  | 'nameAsc'
  | 'nameDesc'
  | 'random';

/**
 * Один флаг над хромом экрана «Фото» (спека пп. 97–98): лица и «Люди», вкладки
 * «Портреты / Группами / Без лиц», водяные знаки, пресеты, дубли, «Копировать» — скрыты,
 * но код остаётся: галерея паритета Lumir Lab их не показывает.
 */
export const PHOTO_CHROME_VISIBLE: boolean = false;

export interface ManageScenePhoto {
  id: string;
  thumbnailUrl: string | null;
  isClientPrivate: boolean;
  sortOrder: number;
  // Drives the RAW badge of a tile that has no preview
  originalName: string | null;
  // Tile state: 'processing' = grey square + spinner, 'failed' = the retry tile.
  // 'ready' is the fallback when the server does not send the field.
  processingStatus: 'ready' | 'processing' | 'failed';
  // Renditions the VIEWER needs (s9-ac6): the tile lives on thumbnailUrl alone.
  // Optional like the wire's own columns — a photo whose renditions are still
  // being produced simply has none, and the viewer degrades down the ladder.
  smallUrl?: string | null;
  mediumUrl?: string | null;
  originalUrl?: string | null;
  width?: number | null;
  height?: number | null;
  thumbHash?: string | null;
  dominantColor?: string | null;
}

export interface ManageSceneVideo {
  id: string;
  provider: string;
  url: string;
  embedUrl: string;
  // Uploaded video files only: the file's own address (for the native player and
  // the frame-grab). Null for embed providers, which play via embedUrl.
  fileUrl: string | null;
  // The facade poster pick — one of the gallery's photos (embeds cannot take a
  // true player thumbnail, so the poster shows before play).
  posterPhotoId: string | null;
  posterThumbnailUrl: string | null;
}

export interface ManageScene {
  id: string;
  name: string;
  kind: ManageSceneKind;
  description: string | null;
  isHidden: boolean;
  photoSortMode: PhotoSortMode;
  sortOrder: number;
  photoCount: number;
  videoCount: number;
  // Photos of this scene held by an approved (locked) client list. > 0 — «Удалить сцену»
  // answers with the toast and never opens the window (spec 45). The delete's 409 is still
  // the authority; this count only spares the photographer a pointless dialog.
  // Optional so a fixture may omit it; `toScene` — the ONE producer of a real scene —
  // always sets it, so a live scene never carries undefined here.
  lockedPhotoCount?: number;
  photos: ManageScenePhoto[];
  videos: ManageSceneVideo[];
}

// Шапка галереи предлагает три режима; сцена по-прежнему может нести любой из
// восьми серверных — старый режим просто ПОКАЗЫВАЕМ ближайшим из трёх, значение
// сцены не трогаем, пока фотограф сам не выберет режим.
export type GallerySortMode = 'nameAsc' | 'shotAtOld' | 'manual';

// Порядок загрузки — тоже порядок по времени, поэтому uploaded* приравниваем к дате
// съёмки (сервер так же сворачивает легаси 'shotAt' → 'shotAtOld'). «Случайно» —
// не сортировка по признаку фото, ближайшее к нему — «Вручную».
const SORT_MODE_FOLD: Record<PhotoSortMode, GallerySortMode> = {
  manual: 'manual',
  random: 'manual',
  nameAsc: 'nameAsc',
  nameDesc: 'nameAsc',
  shotAtNew: 'shotAtOld',
  shotAtOld: 'shotAtOld',
  uploadedNew: 'shotAtOld',
  uploadedOld: 'shotAtOld',
};

// Одна галерея — один режим: сцены разошлись (или хоть одна вручную) → «Вручную».
export function galleryPhotoSortMode(scenes: readonly ManageScene[]): GallerySortMode {
  const folded = scenes.filter(scene => scene.kind === 'photo').map(scene => SORT_MODE_FOLD[scene.photoSortMode]);
  return folded.length > 0 && folded.every(mode => mode === folded[0]) ? folded[0] : 'manual';
}
